import { computed, reactive } from "vue";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useModelViewStore } from "@editor/views/model/modelView.store";
import { useRelationStore } from "./relations.store";
import { useElements } from "../element/elements";
import { getUid, isUidOfType } from "@/helpers/utility/Id";
import { useSceneTree } from "../scene/sceneTree";
import { isElementTreeNode } from "../sceneElement/sceneElementFunctions";
import { notNull } from "@/helpers/utility/Type";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";

export const useRelationBuilder = function () {
  const selectionStore = useSelectionStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const modelViewStore = useModelViewStore();
  const relationStore = useRelationStore();
  const elements = useElements();
  const sceneTree = useSceneTree();

  const baseElement = computed(() => {
    const impactSourceElement = modelViewStore.impactSourceElement;
    if (impactSourceElement) {
      return elements.byUid[impactSourceElement.uid];
    }

    const selectedElements = selectionStore.selectedItems.filter(
      (i) => i.type == "target" || i.type == "resource"
    ) as { type: "target" | "resource"; id: number }[];

    const selectedElement = selectedElements?.[0];
    if (selectedElement == null) return null;

    return elements.byUid[getUid(selectedElement)];
  });

  const selectedElements = computed(() => {
    const selectedTargets = selectionStore.selected.target || [];
    const targetIds = selectedTargets.filter(
      (id) => id != baseElement.value?.id || baseElement.value.type != "target"
    );
    const targets = targetStore.getArrayByIds(targetIds);
    const selectedResources = selectionStore.selected.resource || [];
    const resourceIds = selectedResources.filter(
      (id) => id != baseElement.value?.id || baseElement.value.type != "resource"
    );
    const resources = resourceStore.getArrayByIds(resourceIds);
    const list: (TargetItem | ResourceItem)[] = [...targets, ...resources];

    
    const selectedNodeUids = [...selectionStore.selectedUids].filter(isUidOfType("sceneNode"));
    const selectedElementNodes = selectedNodeUids.map((uid) => sceneTree.treeNodes.get(uid)).filter(notNull).filter((node) => isElementTreeNode(node));
    
    for (const treeNode of selectedElementNodes) {
      const sceneNode = treeNode.node;
      const element = elements.byUid[sceneNode.data.elementUid];
      if (element.type != "index") list.push(element);
    }

    return list;
  });

  const endElement = computed(() => {
    if (selectedElements.value.length === 1) return selectedElements.value[0];
    return null;
  });

  const relationDraftId = computed(() => {
    if (endElement.value == null || baseElement.value == null) {
      return null;
    }
    return `${baseElement.value.type}.${baseElement.value.id}-${endElement.value.type}.${endElement.value.id}`;
  });

  const relationDrafts = computed(() => {
    return relationStore.relationDrafts;
  });

  const relationDraft = computed(() => {
    if (relationDraftId.value == null) return null;
    return relationDrafts.value?.[relationDraftId.value];
  });

  function exitRelationBuilder() {
    modelViewStore.impactSourceElement = null;
  }

  return reactive({
    baseElement,
    endElement,
    relationDrafts,
    relationDraftId,
    relationDraft,
    exitRelationBuilder
  });
};
